/**
 * useUpcomingEventsDropdown
 *
 * Standalone loadOptions function for the upcoming events/tasks paginated dropdown,
 * designed for use with react-select-async-paginate.
 *
 * Calls POST /list-events-v2 with is_upcoming_dropdown=true.
 *
 * Pagination strategy: page-based
 *   Request  → `upcoming_page` (1-based integer)
 *   Response → `page_count` tells us total pages; hasMore = currentPage < pageCount
 *
 * The `additional` object carries the next page number between loads: { page: number }
 *
 * Note: The API does not support keyword search filtering. The `search` argument from
 * react-select-async-paginate is intentionally ignored.
 */

import { performRequestSecond } from 'common/apiHandler';
import apiEndPoints from 'common/apiEndPoints';
import { getTenantDetails } from 'utils/utils';

const DEFAULT_PAGE_SIZE = 10;

/**
 * Formats a date string into a short human-readable form.
 * Falls back gracefully if the date is invalid.
 *
 * @param {string} dateStr - ISO 8601 date string
 * @returns {string}
 */
const formatDropdownDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
};

/**
 * Converts a raw upcoming-events API item into a react-select option shape.
 *
 * @param {Object} item - Raw item from `upcoming_events[]`
 * @returns {{ label: string, value: string, ...item }}
 */
export const toEventOption = (item) => {
  if (item.calendar_type === 'task') {
    return {
      label: item.task_name || 'Untitled Task',
      value: item.task_id,
      ...item,
    };
  }
  return {
    label: item.event_title || 'Untitled Event',
    value: item.event_id,
    event_name: item.event_title,
    ...item,
  };
};

/**
 * Fetches one page of upcoming events/tasks from the API.
 *
 * @param {Object} params
 * @param {number}      params.page          - 1-based page number
 * @param {number}      params.pageSize      - Records per page (1–1000)
 * @param {string|null} params.calendarType  - 'event' | 'task' | null (both)
 * @param {string}      params.timezone      - IANA timezone string
 * @returns {Promise<{ options: Array, hasMore: boolean, additional: { page: number } }>}
 */
const fetchUpcomingEventsPage = async ({
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  calendarType = null,
  timezone = 'UTC',
  caseId = null,
}) => {
  const [tenantId] = getTenantDetails();

  const queryParams = new URLSearchParams({
    tenant_id: tenantId,
    is_upcoming_dropdown: 'true',
    tz: timezone,
    upcoming_limit: String(pageSize),
    upcoming_page: String(page),
  });

  const body = { show_firm_events: true, case_id: caseId };
  if (calendarType) body.calendar_type = calendarType;

  const url = `${apiEndPoints.calendar.listEventsV2}?${queryParams.toString()}`;
  const response = await performRequestSecond(apiEndPoints.methodType.POST, url, body, '', true, true);

  const items = response?.data?.upcoming_events || [];
  const pageCount = response?.data?.page_count || 1;

  return {
    options: items.map(toEventOption),
    hasMore: page < pageCount,
    additional: { page: page + 1 },
  };
};

/**
 * loadOptions adapter for react-select-async-paginate.
 *
 * Signature expected by AsyncPaginate:
 *   (searchQuery, loadedOptions, additional) => Promise<{ options, hasMore, additional }>
 *
 * @param {string} _search         - Ignored — API does not support keyword filtering
 * @param {Array}  _loadedOptions  - Already-loaded options (unused)
 * @param {Object} additional      - { page: number } cursor carried between page loads
 * @param {Object} config          - { pageSize?, calendarType?, timezone? }
 */
export const loadUpcomingEventOptions = async (_search, _loadedOptions, additional, config = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, calendarType = null, timezone = 'UTC', caseId = null } = config;
  const page = additional?.page || 1;

  try {
    return await fetchUpcomingEventsPage({ page, pageSize, calendarType, timezone, caseId });
  } catch (error) {
    console.error('Error loading upcoming event options:', error);
    return { options: [], hasMore: false, additional: { page: 1 } };
  }
};

/**
 * Converts a raw event_attachment / task_attachment array (as persisted in API responses)
 * back into the react-select option shape expected by EventSelect as its `value` prop.
 *
 * Use this when pre-populating an EventSelect from saved API data.
 *
 * @param {Array} items - Raw API items (events or tasks)
 * @returns {Array<{ label: string, value: string, ...item }>}
 */
export const toEventOptions = (items = []) => items.map(toEventOption);
